import { Dropdown } from '@statsbomb/kitbag-components';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { styled } from 'styled-components';
import { useAtomValue } from 'jotai';
import themeHelper from '../../utils/themeHelper';
import MetadataSelections from '../MetadataSelections';
import { selectedMatchIdAtom } from '../../atoms/selectedMetadata';
import {
  AlignmentIssueDropdownValues,
  AlignmentStatusDropdownValues,
} from '../../constants/alignment';
import { videoInfoPropTypes } from '../../types/videoInfo';
import { videoReferencePropTypes } from '../../types/videoReference';
import {
  generateAlignmentIssueOptions,
  generateAlignmentStatusOptions,
  generateCompetitionOptions,
  generateSeasonOptions,
} from './VideoFilters.helper';
import { FilterItemWrapper } from './VideoFilters.styles';

export const FiltersContainer = styled.div`
  display: flex;
  gap: ${themeHelper.spacing(1)};
  padding-top: ${themeHelper.spacing(1)};
  padding-left: ${themeHelper.spacing(1)};
  padding-right: ${themeHelper.spacing(1)};
`;

const VideoSearchSelectionContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${themeHelper.spacing()};
  min-width: 30vw;
  max-width: 60vw;
`;

/*
 * Component to filter videos by competition, alignment issue, alignment status, and match id
 * Visibility of the filters is controlled by passing in the setters for each filter
 */
export function VideoFilters({
  competitions,
  seasons,
  alignmentIssue,
  alignmentStatus,
  setCompetitions,
  setSeasons,
  setAlignmentIssue,
  setAlignmentStatus,
  setMatchId,
  videos,
}) {
  const intl = useIntl();

  const selectedMatchId = useAtomValue(selectedMatchIdAtom);
  const alignmentIssueOptions = generateAlignmentIssueOptions(intl);
  const alignmentStatusOptions = generateAlignmentStatusOptions(intl);
  alignmentIssueOptions.sort((a, b) => a.label.localeCompare(b.label));
  alignmentStatusOptions.sort((a, b) => a.label.localeCompare(b.label));

  const seasonOptions = useMemo(
    () => (videos ? generateSeasonOptions(videos) : []),
    [videos],
  );
  seasonOptions.sort((a, b) => a.label.localeCompare(b.label));

  const competitionOptions = useMemo(
    () => (videos ? generateCompetitionOptions(videos) : []),
    [videos],
  );
  competitionOptions.sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    if (setMatchId) {
      setMatchId(selectedMatchId);
    }
  }, [selectedMatchId]);

  return (
    <FiltersContainer>
      {setCompetitions && (
        <FilterItemWrapper>
          <Dropdown
            id="competitions-dropdown"
            label="Competitions"
            labelPosition="top"
            onChange={(selected) => setCompetitions(selected)}
            value={competitions}
            options={competitionOptions}
            variant="primary"
            isSearchable
            isMulti
          />
        </FilterItemWrapper>
      )}

      {setSeasons && (
        <FilterItemWrapper>
          <Dropdown
            id="seasons-dropdown"
            label="Seasons"
            labelPosition="top"
            onChange={(selected) => setSeasons(selected)}
            value={seasons}
            options={seasonOptions}
            variant="primary"
            isSearchable
            isMulti
          />
        </FilterItemWrapper>
      )}

      {setAlignmentIssue && (
        <FilterItemWrapper>
          <Dropdown
            id="alignment-issue-dropdown"
            label="Issue"
            labelPosition="top"
            onChange={(selected) => setAlignmentIssue(selected)}
            value={alignmentIssue}
            options={alignmentIssueOptions}
            variant="primary"
            isSearchable
          />
        </FilterItemWrapper>
      )}

      {setAlignmentStatus && (
        <FilterItemWrapper>
          <Dropdown
            id="alignment-status-dropdown"
            label="Status"
            labelPosition="top"
            onChange={(selected) => setAlignmentStatus(selected)}
            value={alignmentStatus}
            options={alignmentStatusOptions}
            variant="primary"
            isSearchable
          />
        </FilterItemWrapper>
      )}

      {setMatchId && (
        <FilterItemWrapper>
          <VideoSearchSelectionContainer>
            <MetadataSelections.MatchId id="matchId" required />
          </VideoSearchSelectionContainer>
        </FilterItemWrapper>
      )}
    </FiltersContainer>
  );
}

const dropdownPropTypes = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

const alignmentIssuePropTypes = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOf(Object.values(AlignmentIssueDropdownValues))
    .isRequired,
});

const alignmentStatusPropTypes = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOf(Object.values(AlignmentStatusDropdownValues))
    .isRequired,
});

const videoRequiringAlignmentCheckPropTypes = PropTypes.shape({
  videoInfo: videoInfoPropTypes.isRequired,
  videoReference: videoReferencePropTypes.isRequired,
});

VideoFilters.propTypes = {
  alignmentIssue: alignmentIssuePropTypes,
  alignmentStatus: alignmentStatusPropTypes,
  competitions: PropTypes.arrayOf(dropdownPropTypes),
  seasons: PropTypes.arrayOf(dropdownPropTypes),
  setAlignmentIssue: PropTypes.func,
  setAlignmentStatus: PropTypes.func,
  setCompetitions: PropTypes.func,
  setMatchId: PropTypes.func,
  setSeasons: PropTypes.func,
  videos: PropTypes.arrayOf(videoRequiringAlignmentCheckPropTypes),
};

VideoFilters.defaultProps = {
  alignmentIssue: undefined,
  alignmentStatus: undefined,
  competitions: undefined,
  seasons: undefined,
  setAlignmentIssue: undefined,
  setAlignmentStatus: undefined,
  setCompetitions: undefined,
  setMatchId: undefined,
  setSeasons: undefined,
  videos: [],
};

export default VideoFilters;
